import { useEffect, useState } from 'react'
import { Delete, FiberManualRecord } from '@mui/icons-material'
import {
  CircularProgress,
  IconButton,
  List,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useDeleteRowValueFlag } from '@/service-library/hooks/row-value-flags'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUserVisible from '@/services/hooks/useIsSuperUserVisible'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { RowValueFlag } from '@/types/flags'
import { flagDeleteMutateOptions } from './FlagListItem'
import FlagLongDescription from './FlagLongDescription'
import useFlagColorMap from './useFlagColorMap'

type RowValueFlagsListProps = {
  rowValueFlags: RowValueFlag[]
  noPadding?: boolean
}

const QUERIES_TO_INVALIDATE = [
  queryKeys.rowValueFlags.lists(),
  queryKeys.documents.lists(),
  queryKeys.documentRows.lists(),
  queryKeys.documentRowValues.lists(),
]

export default function FlagsBulletList({
  rowValueFlags,
  noPadding,
}: RowValueFlagsListProps) {
  const queryClient = useQueryClient()
  const colorMap = useFlagColorMap()
  const isSuperUserVisible = useIsSuperUserVisible()
  const [isDismissing, setIsDismissing] = useState(false)

  const { deleteRowValueFlag } = useDeleteRowValueFlag({
    sideEffectQueryKeys: QUERIES_TO_INVALIDATE,
    ...flagDeleteMutateOptions,
    onSettled: () => setIsDismissing(false),
  })

  useEffect(() => {
    const incompleteFlags = rowValueFlags.some(({ flag_level }) => !flag_level)
    if (incompleteFlags) {
      QUERIES_TO_INVALIDATE.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey })
      })
    }
  }, [queryClient, rowValueFlags])

  return (
    <List
      component="ul"
      sx={{
        pl: noPadding ? 0 : 1,
        width: 0,
        minWidth: '100%',
      }}
    >
      {rowValueFlags.map((flag) => {
        // [sc-21113] - When updating the cache, some flags come as "update" (partial), and miss the flag_level.
        const flagLevel = flag.flag_level || 'error'
        const isDismissible = flagLevel !== 'error' && flagLevel !== 'failure'
        const colorBase = colorMap[flagLevel].base
        return (
          <Stack key={flag.id} direction="row" alignItems="center">
            <Tooltip
              title={
                <FlagLongDescription longDescription={flag.long_description} />
              }
              disableInteractive
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                component="li"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                }}
              >
                <FiberManualRecord
                  sx={{
                    fontSize: 12,
                    flexShrink: 0,
                    color: colorBase,
                    transform: 'translateY(-1px)',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: colorBase,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {flag.short_description || flag.flag_type?.short_description}
                </Typography>
              </Stack>
            </Tooltip>
            {isDismissible || isSuperUserVisible ? (
              !isDismissing ? (
                <Tooltip title="Dismiss">
                  <IconButton
                    edge="end"
                    size="small"
                    sx={{ transform: 'translateX(4px)' }} // Slight adjustment for positioning
                    onClick={() => {
                      setIsDismissing(true)
                      deleteRowValueFlag(flag.id)
                    }}
                  >
                    <Delete
                      sx={{
                        fontSize: 16,
                        color: isDismissible
                          ? undefined
                          : SUPER_USER_ONLY_COLOR,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : (
                <CircularProgress size={12} color="neutral" sx={{ m: 1 }} />
              )
            ) : undefined}
          </Stack>
        )
      })}
    </List>
  )
}
